/**
 * v145.27 — Storefront Cart Context
 * ---------------------------------
 * Per-storefront cart that lets buyers accumulate multiple products and
 * check out once. Scoped by `creatorSlug` — different shops keep their
 * own carts.
 *
 * v145.28 — Cart persistence: state is mirrored into localStorage under
 * key `linkola_cart_v1_{slug}` so a buyer can close the tab and come
 * back later without losing what they added. Cleared on `clear()` (or
 * successful checkout, which calls clear()).
 *
 * State shape:
 *   items: { [product_id]: { product, units } }
 *
 * Helpers exposed:
 *   add(product), setUnits(product_id, units), remove(product_id),
 *   clear(), inCart(product_id), count, subtotal, restore(items[])
 *
 * Consumers:
 *   • `<StorefrontCartProvider slug>` — wraps a storefront page / block
 *   • `useStorefrontCart()` — read state + call helpers from any child
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const CartContext = createContext(null);

const STORAGE_PREFIX = "linkola_cart_v1_";

function storageKey(slug) {
  return `${STORAGE_PREFIX}${(slug || "unknown").toLowerCase()}`;
}

function loadFromStorage(slug) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Basic shape guard: every entry must have a product with a product_id + a units > 0.
    const clean = {};
    for (const [pid, entry] of Object.entries(parsed)) {
      if (!entry?.product?.product_id) continue;
      const u = Math.min(100, Math.max(1, Number(entry.units) || 0));
      if (u > 0) clean[pid] = { product: entry.product, units: u };
    }
    return clean;
  } catch {
    return {};
  }
}

function saveToStorage(slug, items) {
  if (typeof window === "undefined") return;
  try {
    const keys = Object.keys(items || {});
    if (keys.length === 0) {
      window.localStorage.removeItem(storageKey(slug));
    } else {
      window.localStorage.setItem(storageKey(slug), JSON.stringify(items));
    }
  } catch {
    /* quota / private mode — silent, cart still works in memory */
  }
}

export function StorefrontCartProvider({ slug, children }) {
  const [items, setItems] = useState(() => loadFromStorage(slug));
  const hydratedRef = useRef(false);

  // Re-hydrate when slug changes (rare — different shop opens in the
  // same SPA session).
  useEffect(() => {
    setItems(loadFromStorage(slug));
    hydratedRef.current = true;
  }, [slug]);

  // Persist every mutation.
  useEffect(() => {
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    saveToStorage(slug, items);
  }, [slug, items]);

  const add = useCallback((product, units = 1) => {
    if (!product?.product_id) return;
    setItems((prev) => {
      const existing = prev[product.product_id];
      return {
        ...prev,
        [product.product_id]: {
          product,
          units: Math.min(100, (existing?.units || 0) + units),
        },
      };
    });
  }, []);

  const setUnits = useCallback((product_id, units) => {
    const n = Math.max(0, Math.min(100, Number(units) || 0));
    setItems((prev) => {
      if (!prev[product_id]) return prev;
      if (n === 0) {
        const clone = { ...prev };
        delete clone[product_id];
        return clone;
      }
      return { ...prev, [product_id]: { ...prev[product_id], units: n } };
    });
  }, []);

  const remove = useCallback((product_id) => {
    setItems((prev) => {
      const clone = { ...prev };
      delete clone[product_id];
      return clone;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const inCart = useCallback((product_id) => !!items[product_id], [items]);

  // v145.28 — Bulk restore for the cart-reminder flow. Accepts the same
  // items[] shape the /public/storefront/cart/{id} endpoint returns.
  const restore = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const next = {};
    for (const r of rows) {
      if (!r?.product_id) continue;
      next[r.product_id] = {
        product: {
          product_id: r.product_id,
          title: r.title,
          price: r.unit_price_inr,
          currency: "INR",
          image_url: r.image_url,
          kind: "product",
          creator_user_id: r.creator_user_id,
        },
        units: Math.min(100, Math.max(1, Number(r.units) || 1)),
      };
    }
    setItems(next);
  }, []);

  const list = useMemo(() => Object.values(items), [items]);
  const count = useMemo(() => list.reduce((n, i) => n + i.units, 0), [list]);
  const subtotal = useMemo(
    () => list.reduce((s, i) => s + Number(i.product?.price || 0) * i.units, 0),
    [list],
  );

  const value = useMemo(
    () => ({ slug, items, list, count, subtotal, add, setUnits, remove, clear, inCart, restore }),
    [slug, items, list, count, subtotal, add, setUnits, remove, clear, inCart, restore],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useStorefrontCart() {
  return useContext(CartContext); // returns null when not wrapped
}
